import React, { Component } from 'react';
import withHooks from '../../core/withHooks';
import Inherit from '../../core/inherit';
import TreasuryController from './treasury.controller';
import Page from '../common/page/Page';
import NoPermissionFallback from '../common/roleGuard/elements/NoPermissionFallback';
import { SCOPES } from '../../constants/scopes.constants';
import GLAccountSection from './elements/GLAccountSection';
import TransactionHistorySection from './elements/TransactionHistorySection';
import { GL_TYPE, PAGE_METADATA, TREASURY_DEFAULT_TIMEFRAME } from './treasury.constants';

/**
 * Cashflow Management — view-only React component.
 *
 * Owns nothing but a tiny slice of orchestrator-level state (loading,
 * active GL tab, period). Every other concern lives in:
 *
 *   - `treasury.controller.js`   → page orchestration + sub-controller
 *                                   instantiation
 *   - `treasury.delegate.js`     → tab / period setters
 *   - `glAccount.controller.js`  → account / funding / kpis / cashflow / aging
 *   - `transactionHistory.controller.js` → paginated transactions list
 *
 * The render is just composition: a `<Page>` shell with the two GL
 * tabs, then a dumb `<GLAccountSection>` + `<TransactionHistorySection>`
 * fed by `getViewProps()` from each sub-controller.
 */
class Treasury extends Inherit(TreasuryController, Component) {
    state = {
        loading: true,
        user: {},
        glType: GL_TYPE.ADVANCE,
        timeFrame: TREASURY_DEFAULT_TIMEFRAME,
        dateRange: null,
    };

    render() {
        const { loading, glType, timeFrame, dateRange } = this.state;

        const period = { timeFrame, dateRange };
        const glViewProps = this.glAccount.getViewProps();
        const txViewProps = this.transactions.getViewProps();
        const accessibleTabs = this.getAccessibleTabs();

        // Screen scope without either GL scope grants nothing to look at.
        // Deny outright instead of falling back to Advance, which would
        // render a full GL account and transaction history under an empty
        // tab strip. Gated on `this.user` so an unhydrated session waits for
        // Page's own loading deferral rather than being denied on a
        // not-yet-filled scope list.
        if (this.user && !accessibleTabs.length) {
            return (
                <Page
                    title={PAGE_METADATA.title}
                    description={PAGE_METADATA.description}
                    scope={SCOPES.TREASURY_VIEW}
                    notificationContextHolder={this.props.notificationContextHolder}>
                    <NoPermissionFallback />
                </Page>
            );
        }

        return (
            <Page
                title={PAGE_METADATA.title}
                description={PAGE_METADATA.description}
                scope={SCOPES.TREASURY_VIEW}
                tabs={accessibleTabs}
                onTabChange={this.handleTabChange.bind(this)}
                notificationContextHolder={this.props.notificationContextHolder}
                isLoading={loading}>
                <GLAccountSection
                    {...glViewProps}
                    glType={glType}
                    period={period}
                    onTimeFrameChange={this.handleTimeFrameChange.bind(this)}
                    onDateRangeChange={this.handleDateRangeChange.bind(this)}
                    onViewOverdueUsers={() => this.props.navigate?.(`/users?glType=${glType}&overdue=true`)}
                />

                <TransactionHistorySection {...txViewProps} />
            </Page>
        );
    }
}

export default withHooks(Treasury);
